// =====================================================
// HOOK: useMyData
// Description: Hook centralisé pour récupérer toutes les données assignées à l'utilisateur connecté
// =====================================================

import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useProspects } from './useProspects';
import { useCrews } from './useCrews';
import { useMyTasks } from './useMyTasks';
import { excludeArchived } from '@/lib/activeCrews';

/**
 * Hook centralisé pour récupérer toutes les données de l'utilisateur connecté
 * - Prospects assignés
 * - Équipages assignés
 * - Tâches assignées
 * - Statistiques calculées
 */
export function useMyData() {
  const { user } = useAuth();
  const userId = user?.id;

  // Charger toutes les données
  const { data: allProspects, loading: loadingProspects } = useProspects();
  const { crews: allCrews, loading: loadingCrews } = useCrews();
  const {
    pendingTasks,
    urgentTasks,
    completeTask,
    deleteTask,
    loading: loadingTasks,
  } = useMyTasks();

  // Filtrer les prospects assignés à l'utilisateur
  const myProspects = useMemo(() => {
    if (!userId) return [];
    return allProspects.filter((prospect) => prospect.contact?.assigned_to === userId);
  }, [allProspects, userId]);

  // Filtrer les équipages assignés à l'utilisateur
  // CRM-12 : useCrews() renvoie tous les statuts — exclure les archivés avant comptage
  const myCrews = useMemo(() => {
    if (!userId) return [];
    return excludeArchived(allCrews).filter((crew) => crew.assigned_to === userId);
  }, [allCrews, userId]);

  // Calculer les statistiques
  const stats = useMemo(() => {
    // Total des prospects + équipages assignés
    const totalAssigned = myProspects.length + myCrews.length;

    // Équipages avec BED complet ou terminés (statuts "validés")
    const bedCompleteCrews = myCrews.filter(
      (crew) => crew.status === 'bed_complete' || crew.status === 'completed'
    );

    // Taux de conversion : (équipages BED complet ou terminés / total équipages) × 100
    const conversionRate =
      myCrews.length > 0 ? Math.round((bedCompleteCrews.length / myCrews.length) * 100) : 0;

    // Total de tâches planifiées (non complétées)
    const totalPendingTasks = pendingTasks.length;

    // Nombre de tâches urgentes
    const totalUrgentTasks = urgentTasks.length;

    return {
      totalAssigned,
      prospectsCount: myProspects.length,
      crewsCount: myCrews.length,
      bedCompleteCrewsCount: bedCompleteCrews.length,
      conversionRate,
      totalPendingTasks,
      totalUrgentTasks,
    };
  }, [myProspects, myCrews, pendingTasks, urgentTasks]);

  const loading = loadingProspects || loadingCrews || loadingTasks;

  return {
    myProspects,
    myCrews,
    myTasks: pendingTasks,
    stats,
    loading,
    completeTask,
    deleteTask,
  };
}
